import { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { supportRequestApi } from "../services/supportRequestApi";

const CATEGORIES = [
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "question", label: "Question" },
  { value: "other", label: "Other" },
];

const MAX_FILES = 5;

const INITIAL_FORM = {
  category: "",
  subject: "",
  description: "",
  stepsToReproduce: "",
};

const ALLOWED_TYPES = [
  "image/",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const isFileAllowed = (file) =>
  ALLOWED_TYPES.some((t) => (t.endsWith("/") ? file.type.startsWith(t) : file.type === t));

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      backgroundColor: "#ffffff",
      zIndex: -1,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    zIndex: 1,
    backgroundColor: "#ffffff",
    padding: "0 6px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "primary.main",
  },
};

export default function RaiseRequestDialog({ open, onClose }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError("");
  };

  const handleFileUpload = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) {
      return;
    }

    const remaining = MAX_FILES - files.length;
    if (selected.length > remaining) {
      setError(`You can attach up to ${MAX_FILES} files. ${remaining} slot(s) remaining.`);
      return;
    }

    const invalid = selected.filter((f) => !isFileAllowed(f));
    if (invalid.length) {
      setError("Only images, PDFs, and Word documents are allowed");
      return;
    }

    const oversized = selected.filter((f) => f.size > 10 * 1024 * 1024);
    if (oversized.length) {
      setError("Each file must be under 10MB");
      return;
    }

    setFiles((prev) => [...prev, ...selected]);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!form.category || !form.subject || !form.description) {
      setError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await supportRequestApi.create({
        ...form,
        attachments: files,
        pageUrl: window.location.href,
        browserInfo: navigator.userAgent,
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setFiles([]);
    setError("");
    setSuccess(false);
    setSubmitting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(86, 100, 210, 0.08)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              backgroundColor: "rgba(86, 100, 210, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SupportAgentIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary" }}>
            Raise a Support Request
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 3,
          borderTop: "1px solid #eeeffa",
          borderBottom: "1px solid #eeeffa",
        }}
      >
        {success ? (
          <Alert
            severity="success"
            sx={{
              my: 2,
              borderRadius: 2,
              backgroundColor: "#dcfce7",
              color: "#15803d",
              "& .MuiAlert-icon": { color: "#15803d" },
            }}
          >
            Request submitted successfully! We will get back to you soon.
          </Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1.5 }}>
            <TextField
              select
              label="Category"
              value={form.category}
              onChange={handleChange("category")}
              required
              size="small"
              fullWidth
              sx={textFieldStyles}
              InputLabelProps={{ shrink: true }}
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Subject"
              value={form.subject}
              onChange={handleChange("subject")}
              required
              size="small"
              fullWidth
              placeholder="Brief summary of your request"
              sx={textFieldStyles}
            />

            <TextField
              label="Description"
              value={form.description}
              onChange={handleChange("description")}
              required
              multiline
              rows={4}
              fullWidth
              placeholder="Describe your request in detail..."
              sx={textFieldStyles}
            />

            {form.category === "bug" && (
              <TextField
                label="Steps to Reproduce"
                value={form.stepsToReproduce}
                onChange={handleChange("stepsToReproduce")}
                multiline
                rows={3}
                fullWidth
                placeholder={"1. Go to...\n2. Click on...\n3. Observe that..."}
                sx={textFieldStyles}
              />
            )}

            {/* Attachments Section */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "grey.600" }}>
                Attachments (Optional, up to {MAX_FILES} files)
              </Typography>

              {/* File list */}
              {files.length > 0 && (
                <Stack spacing={1} sx={{ mb: 1.5 }}>
                  {files.map((file, idx) => (
                    <Box
                      key={`${file.name}-${idx}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid rgba(86, 100, 210, 0.15)",
                        borderRadius: 2,
                        backgroundColor: "grey.50",
                      }}
                    >
                      {file.type?.startsWith("image/") ? (
                        <Box
                          component="img"
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <InsertDriveFileIcon
                          sx={{ color: "primary.main", fontSize: 28, flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {(file.size / 1024).toFixed(0)} KB
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveFile(idx)}>
                        <DeleteIcon sx={{ fontSize: 18, color: "error.main" }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Upload button */}
              {files.length < MAX_FILES && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    p: 2.5,
                    border: "1px dashed rgba(86, 100, 210, 0.3)",
                    borderRadius: 2,
                    backgroundColor: "rgba(86, 100, 210, 0.01)",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                    Upload images, PDFs, or Word documents
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    component="label"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "rgba(86, 100, 210, 0.4)",
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: "rgba(86, 100, 210, 0.04)",
                      },
                    }}
                  >
                    {files.length > 0 ? "Add More Files" : "Upload Files"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  {files.length > 0 && (
                    <Chip
                      label={`${files.length} / ${MAX_FILES} files`}
                      size="small"
                      sx={{ color: "text.secondary", backgroundColor: "grey.100" }}
                    />
                  )}
                </Box>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
              "&:hover": {
                backgroundColor: "rgba(100, 116, 139, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: 2,
              backgroundColor: "primary.main",
              color: "#ffffff",
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              boxShadow: "0 4px 12px rgba(86, 100, 210, 0.2)",
              "&:hover": {
                backgroundColor: "#434fbc",
                boxShadow: "0 6px 16px rgba(86, 100, 210, 0.3)",
              },
            }}
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
